/**
 * `/rates`: the commission schedule (what each product earns this partner,
 * grouped like the site's services) and how a fee is earned and paid, all
 * from `GET /api/partner/program`.
 */
import { Chip } from "@nt/ui/chip";
import { PageHeader } from "@nt/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nt/ui/table";
import { Fragment } from "react";

import { QueryView } from "@/components/query-view";
import { Section } from "@/components/section";
import { money } from "@/lib/format";
import { useGet } from "@/lib/queries";
import type { FeeGroup, ProgramData, ProgramFee } from "@/lib/types";

/** Table order and headings; a product the API cannot place lands last. */
const GROUPS: ReadonlyArray<{ group: FeeGroup; title: string }> = [
  { group: "formation", title: "Company formation" },
  { group: "services", title: "Ongoing services" },
  { group: "tax", title: "Tax filings" },
  { group: "other", title: "Other services" },
];

const days = (n: number) => `${n} ${n === 1 ? "day" : "days"}`;

function Fact({ term, children }: { term: string; children: string }) {
  return (
    <div className="grid gap-1">
      <dt className="text-sm font-medium text-foreground">{term}</dt>
      <dd className="text-sm text-muted-foreground">{children}</dd>
    </div>
  );
}

function FeeCell({ fee }: { fee: ProgramFee }) {
  const custom = fee.feeCents !== fee.standardCents;
  return (
    <TableCell className="pr-6 text-right align-top">
      <div className="flex flex-col items-end gap-1">
        <span className="font-medium text-foreground tabular-nums">
          {money(fee.feeCents)}
        </span>
        {custom ? (
          <span className="flex flex-wrap items-center justify-end gap-1.5">
            <Chip tone="info" size="sm">
              Custom rate
            </Chip>
            <span className="text-xs text-muted-foreground tabular-nums">
              Standard {money(fee.standardCents)}
            </span>
          </span>
        ) : null}
      </div>
    </TableCell>
  );
}

/** The schedule: one table, a heading row per group, highest fee first. */
function Schedule({ fees }: { fees: ProgramFee[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="pl-6">Service your customer buys</TableHead>
          <TableHead className="pr-6 text-right">You earn</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {GROUPS.map(({ group, title }) => {
          const rows = fees
            .filter((f) => f.group === group)
            .sort((a, b) => b.feeCents - a.feeCents);
          if (rows.length === 0) return null;
          return (
            <Fragment key={group}>
              <TableRow className="hover:bg-transparent">
                <th
                  colSpan={2}
                  scope="colgroup"
                  data-slot="rates-group"
                  className="bg-muted px-6 py-2 text-left text-xs font-medium tracking-wide text-muted-foreground uppercase"
                >
                  {title}
                </th>
              </TableRow>
              {rows.map((fee) => (
                <TableRow key={fee.key}>
                  {/* normal-nums: Inter's tabular figures widen a hyphen to a digit's width. */}
                  <TableCell className="pl-6 align-top whitespace-normal text-foreground normal-nums">
                    <span>{fee.product}</span>
                  </TableCell>
                  <FeeCell fee={fee} />
                </TableRow>
              ))}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

export function RatesView({ program }: { program: ProgramData }) {
  const top = [...program.fees].sort((a, b) => b.feeCents - a.feeCents)[0];
  return (
    <>
      <Section
        title="Commission schedule"
        description="What you earn when a customer you referred buys each service. Fixed amounts in USD."
        contentClassName="px-0"
      >
        {program.fees.length === 0 ? (
          <p className="px-6 text-sm text-muted-foreground">
            No product earns a fee right now. If you think this is wrong, write
            to support@nonresident.tax and we will check your rates.
          </p>
        ) : (
          <div className="grid gap-4">
            <Schedule fees={program.fees} />
            {top ? (
              <p className="mx-6 rounded-control bg-muted px-4 py-3 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Example: </span>
                someone clicks your link, signs up and buys {
                  top.product
                } within {days(program.windowDays)}. You earn{" "}
                {money(top.feeCents)}, payable after the {program.holdDays}-day
                hold.
              </p>
            ) : null}
          </div>
        )}
      </Section>
      <Section
        title="How you earn"
        description="The rules behind every fee. The partner terms have the full text."
        actions={
          <a
            href={program.termsUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Read the partner terms
          </a>
        }
      >
        <dl className="grid gap-5 sm:grid-cols-2">
          <Fact term="One fee per customer">
            You earn once for each customer you refer, from their first paid
            order, at the rate for what they buy in it.
          </Fact>
          <Fact term="Who counts as your customer">
            {`Someone who clicks your link, then signs up and buys within ${days(program.windowDays)} of the click, or who uses your discount code.`}
          </Fact>
          <Fact term="Hold before a fee is payable">
            {`${days(program.holdDays)} after the order. If the customer is refunded or disputes the payment, the fee is taken back.`}
          </Fact>
          <Fact term="When we pay">
            {`On ${program.payoutDay}, once your payable balance is at least ${money(program.minPayoutCents)}.`}
          </Fact>
        </dl>
      </Section>
    </>
  );
}

export function Rates() {
  const query = useGet<ProgramData>("program", "/api/partner/program");
  return (
    <>
      <PageHeader
        title="Rates"
        description="Your commission schedule and how each fee is earned and paid."
      />
      <QueryView query={query} what="your rates" rows={6}>
        {(program) => <RatesView program={program} />}
      </QueryView>
    </>
  );
}
